import FeeDescriptionSectionController from '../../fee_description_modal/common/section_controller'

// 要介護度と負担割合が揃うまで介護保険料は算定できない。サーバ描画の初期値と同じ未確定表示に戻す。
// 0 を入れると「＋ 介護保険料（0万円）」＝介護保険料は0円、と読めてしまう
// （合計に含めていた旧UIでは合計に埋もれていたが、合計から外した現UIでは断定に見える）。
const UNDETERMINED_KAIGO_HOKEN = '-'

// 料金プランセクションの絞り込み・件数表示・全件表示・介護保険料の反映。
// PC/SP の component_controller はこのクラスを継承して使う
// （片方だけ直されてPCとSPで件数計算が食い違う不具合を防ぐため）。
//
// 費目解説モーダルを開く openFeeDescription は基底クラス側にある（SPのみ使う）。
export default class extends FeeDescriptionSectionController {
  static targets = ['roomTypeSelect', 'careLevelSelect', 'careInsuranceRateSelect', 'plan', 'planNone', 'plansOpen', 'plansClose', 'kaigoHoken', 'step1', 'step2', 'matchedCount', 'visibleCount', 'totalCount']
  static values = {
    unit: Array,
    category: Number,
    areaPrice: Number,
    // 折りたたみ時の表示件数。サーバ側（Plans::Common::Component#limit）が
    // 初期描画で付ける hidden と同じ値を使わないと、初期表示が一瞬で畳み直される
    viewLimit: { type: Number, default: 3 },
  }

  initialize () {
    this.expanded = false
  }

  // 初期の組み立ては initialize ではなく connect で行う。
  // Stimulus は data-action の配線（bindingObserver.start）より前に initialize を呼ぶため、
  // initialize で「全N件のプランを見る」を可視にすると、ボタンは見えているのに
  // クリックハンドラがまだ付いていない一瞬が生まれ、その間のクリックが握り潰される。
  // connect は配線後に呼ばれるので、可視になった時点で必ず押せる。
  // 再接続時に呼び直されても、絞り込み条件と this.expanded から組み立て直すだけで副作用はない。
  connect () {
    // applyFilter 内の updateEmptyState が「該当プランなし」の表示可否を決めるため、
    // ここで planNone を無条件に隠さないこと（隠すと該当0件の表示が打ち消される）
    this.applyFilter()

    if (this.hasCareLevelSelectTarget && this.hasCareInsuranceRateSelectTarget) {
      this.filterBySelect()
      // ブラウザバックやリロードでセレクトの値が復元されると、change は発火しないまま
      // 「要介護1 / 1割負担」が選択済みの状態で接続される。介護保険料を合計から外した現UIでは
      // セルが - のまま残るのが読み手に見えるため、接続時点の選択値から組み立て直す。
      // 両方が未選択なら - を入れ直すだけでサーバ描画と同じになる。
      this.changeNursingInsurance()
    }
  }

  get className () {
    return this.element.dataset.classname
  }

  // 部屋タイプの選択値。未選択のときは空配列（＝絞り込みなし）
  get checkedRoomCategories () {
    return this.roomTypeSelectTargets
      .filter(element => element.checked)
      .map(element => Number(element.value))
  }

  // 絞り込み条件に一致するカード
  get matchedPlans () {
    const checkValues = this.checkedRoomCategories
    if (checkValues.length === 0) return this.planTargets

    return this.planTargets.filter(plan => checkValues.includes(Number(plan.dataset.roomCategory)))
  }

  // 絞り込みと展開状態から表示を組み立て直す。
  // 絞り込みのたびに「全N件のプランを見る」の要否を再評価する
  // （旧実装は絞り込み時に無条件で隠しており、該当4件以上でも展開できなかった）
  applyFilter () {
    const matched = this.matchedPlans

    this.planTargets.forEach(plan => {
      plan.classList.add(`${this.className}::hidden`)
    })

    matched.forEach((plan, index) => {
      if (this.expanded || index < this.viewLimitValue) {
        plan.classList.remove(`${this.className}::hidden`)
      }
    })

    this.updateCount(matched.length)
    this.updateRestButtons(matched.length)
    this.updateEmptyState(matched.length)
  }

  updateCount (matchedCount) {
    const visibleCount = this.expanded ? matchedCount : Math.min(matchedCount, this.viewLimitValue)

    if (this.hasMatchedCountTarget) this.matchedCountTarget.textContent = matchedCount
    if (this.hasVisibleCountTarget) this.visibleCountTarget.textContent = visibleCount
    if (this.hasTotalCountTarget) this.totalCountTarget.textContent = matchedCount
  }

  updateRestButtons (matchedCount) {
    if (!this.hasPlansOpenTarget || !this.hasPlansCloseTarget) return

    if (matchedCount <= this.viewLimitValue) {
      this.plansOpenTarget.style.display = 'none'
      this.plansCloseTarget.style.display = 'none'
    } else if (this.expanded) {
      this.plansOpenTarget.style.display = 'none'
      this.plansCloseTarget.style.display = 'flex'
    } else {
      this.plansOpenTarget.style.display = 'flex'
      this.plansCloseTarget.style.display = 'none'
    }
  }

  updateEmptyState (matchedCount) {
    if (!this.hasPlanNoneTarget) return

    this.planNoneTarget.style.display = matchedCount === 0 ? 'flex' : 'none'
  }

  updateLabel () {
    const selectState = this.careLevelSelectTarget.value && this.careInsuranceRateSelectTarget.value
    const checkState = this.checkedRoomCategories.length > 0

    if (selectState && !checkState) {
      this.step1Target.classList.remove(`${this.className}::current`)
      this.step2Target.classList.add(`${this.className}::current`)
    } else if (selectState && checkState) {
      this.step1Target.classList.remove(`${this.className}::current`)
      this.step2Target.classList.remove(`${this.className}::current`)
    } else {
      this.step1Target.classList.add(`${this.className}::current`)
      this.step2Target.classList.remove(`${this.className}::current`)
    }
  }

  // カードの出し入れはせず、セレクトの状態表示（current）だけを更新する
  filterBySelect () {
    if (this.careLevelSelectTarget.value && !this.careInsuranceRateSelectTarget.value) {
      this.careLevelSelectTarget.classList.remove(`${this.className}::current`)
      this.careInsuranceRateSelectTarget.classList.add(`${this.className}::current`)
    } else if (this.careLevelSelectTarget.value && this.careInsuranceRateSelectTarget.value) {
      this.careLevelSelectTarget.classList.remove(`${this.className}::current`)
      this.careInsuranceRateSelectTarget.classList.remove(`${this.className}::current`)
    } else {
      this.careLevelSelectTarget.classList.add(`${this.className}::current`)
      this.careInsuranceRateSelectTarget.classList.remove(`${this.className}::current`)
    }
    this.updateLabel()
  }

  // 絞り込みを変えたら展開状態は畳み直す（意図的な仕様）。
  // 件数と「全N件のプランを見る」の要否は applyFilter が展開状態から組み立て直すため
  // 展開を維持しても破綻はしないが、畳んだ状態を初期値として見せ直す。
  filterByRoomType () {
    this.expanded = false
    this.applyFilter()
    this.updateLabel()
  }

  changeNursingInsurance () {
    if (!this.careLevelSelectTarget.value || !this.careInsuranceRateSelectTarget.value) {
      this.insertKaigoHoken(UNDETERMINED_KAIGO_HOKEN)
      return
    }

    const unit = this.unitValue.find(element => element.category_id === Number(this.categoryValue) && element.care_level_id === Number(this.careLevelSelectTarget.value))

    if (unit) {
      const kaigoHoken = Number(this.areaPriceValue) * unit.value * (Number(this.careInsuranceRateSelectTarget.value) * 0.1)
      const kaigoHokenRound = Math.round(kaigoHoken / 1000) / 10
      this.insertKaigoHoken(kaigoHokenRound)
    } else {
      // 該当する単位数が無い＝算定できないので、0 ではなく未確定として出す
      this.insertKaigoHoken(UNDETERMINED_KAIGO_HOKEN)
    }
  }

  // 介護保険料は月額合計とは別に表示する（合計は介護保険料を含まない）。
  // 合計セルを「合計＋介護保険料」で上書きすると、
  // 「N万円 ＋ 介護保険料（M万円）」という表記に対して M が二重に足されて読める。
  insertKaigoHoken (kaigoHoken) {
    this.kaigoHokenTargets.forEach(element => {
      element.textContent = kaigoHoken
    })
  }

  // 押したボタンは applyFilter → updateRestButtons で display:none になるため、
  // そのままだとフォーカスが body に飛んでキーボード利用者が読み位置を失う。
  // 対になるボタンへ移す（補足情報の supplement_controller#open / #close と同じ扱い）。
  restOpen (event) {
    this.expanded = true
    this.applyFilter()
    this.focusRestButton(this.plansCloseTarget, event)
  }

  restClose (event) {
    this.expanded = false
    this.applyFilter()
    this.focusRestButton(this.plansOpenTarget, event)
  }

  // 絞り込みの結果が viewLimit 以下になると両方のボタンが display:none になる。
  // 非表示の要素に focus() しても効かずフォーカスは body に残るため、可視のときだけ移す。
  //
  // スクロールの抑止は起動手段で分ける。2つのボタンはテンプレート上プラン一覧の「後ろ」にあり、
  // restOpen では展開でカード3枚ぶん下がるため、両者で望ましい挙動が逆になる。
  //
  //   ポインタ操作: 既定の focus() だとビューポートが「閉じる」まで下がり、
  //     新しく出た4枚目以降を飛び越してしまうので抑止する。
  //   キーボード操作: 抑止するとフォーカスリング（.rest-button の :focus-visible）が
  //     画面外に置かれ、どこへ移ったか見えなくなるため、スクロールさせる。
  //
  // click イベントの detail は、キーボード起動（Enter / Space）なら 0、実際のポインタ操作なら1以上。
  // イベント無しで呼ばれた場合はポインタ相当（抑止する）に倒す。
  focusRestButton (element, event) {
    if (element.style.display === 'none') return

    element.focus({ preventScroll: event?.detail !== 0 })
  }
}
